import { Component, useCallback, useState, type ReactNode } from 'react';
import type { Problem } from './doc/useProject.js';
import { href } from './router.js';

/** The signed-out / no-access message shown wherever a project connection can't proceed.
 * Shared by ProjectPage (desktop and phone) and Share, so a kicked-out or expired member
 * sees the same explanation and way out everywhere, instead of a page stuck loading. */
export function problemBanner(problem: Problem): ReactNode {
  if (problem === 'signed-out') return <p className="banner" role="alert">You've been signed out. <a href="/" onClick={() => location.reload()}>Sign in again</a></p>;
  if (problem === 'no-access') return <p className="banner" role="alert">You no longer have access to this project. <a href={href.projects()}>All projects</a></p>;
  return null;
}

export function useToasts() {
  const [items, setItems] = useState<{ id: number; text: string }[]>([]);
  const toast = useCallback((text: string) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 8000);
  }, []);
  const view = (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <p key={t.id}>{t.text}</p>
      ))}
    </div>
  );
  return { toast, view };
}

/** One broken card must never take the whole board down. */
export class CardErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <div className="card-invalid">Can't display this card.</div> : this.props.children;
  }
}
